import React, { useCallback, useRef } from 'react';
import { Animated, FlatList, Platform, StyleSheet, Text, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import { Colors, Spacing, Typography } from '../theme';
import { useTheme } from '../theme/useTheme';
import { useI18n } from '../i18n';
import type { PlanTemplate } from '../data/planTemplates';
import { PLAN_TEMPLATES } from '../data/planTemplates';

const CARD_GAP = Spacing.md;

interface CardProps {
  template: PlanTemplate;
  cardWidth: number;
  onPress: (template: PlanTemplate) => void;
  compact?: boolean;
}

// Press feel mirrors MapScreen's MetallicIconButton (Animated.spring scale
// on press in/out) — the same "subtle press" idiom already used elsewhere
// in the app, just less aggressive since this is a full card, not an icon.
const TemplateCard: React.FC<CardProps> = ({ template, cardWidth, onPress, compact }) => {
  const { theme } = useTheme();
  const pressAnim = useRef(new Animated.Value(1)).current;

  const handlePressIn = useCallback(() => {
    Animated.spring(pressAnim, { toValue: 0.96, useNativeDriver: true, speed: 30, bounciness: 0 }).start();
  }, [pressAnim]);
  const handlePressOut = useCallback(() => {
    Animated.spring(pressAnim, { toValue: 1, useNativeDriver: true, speed: 20, bounciness: 6 }).start();
  }, [pressAnim]);

  // Same glass-card token pair PlansScreen's own empty-state/section cards
  // use (cardBackground/cardBorder + shadows.card) — kept theme-reactive.
  return (
    <Animated.View style={{ transform: [{ scale: pressAnim }] }}>
      <TouchableOpacity
        activeOpacity={0.9}
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        onPress={() => onPress(template)}
      >
        <View
          style={[
            styles.card,
            compact && styles.compactCard,
            {
              width: cardWidth,
              backgroundColor: theme.colors.cardBackground,
              borderColor: theme.colors.cardBorder,
            },
            theme.isDark ? theme.shadows.card : styles.daylightCard,
          ]}
        >
          <Text style={[styles.emoji, compact && styles.compactEmoji]}>{template.emoji}</Text>
          <View style={compact ? styles.compactCopy : undefined}>
            <Text style={[styles.title, compact && styles.compactTitle, { color: theme.colors.textPrimary }]} numberOfLines={compact ? 1 : 2}>{template.title}</Text>
            {compact ? <Text style={[styles.preview, styles.compactPreview, { color: theme.colors.textSecondary }]} numberOfLines={1}>{template.description}</Text> : <><Text style={styles.metaLine} numberOfLines={1}>{template.vibe.join(' · ')} · {template.location}</Text><Text style={[styles.preview, { color: theme.colors.textSecondary }]} numberOfLines={2}>{template.description}</Text></>}
          </View>
        </View>
      </TouchableOpacity>
    </Animated.View>
  );
};

interface Props {
  onSelectTemplate: (template: PlanTemplate) => void;
  templates?: PlanTemplate[];
}

const PlanTemplateCarousel: React.FC<Props> = ({ onSelectTemplate, templates = PLAN_TEMPLATES }) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const { width } = useWindowDimensions();
  // ~2.2 cards visible at once — the partially-visible next card is what
  // signals "this scrolls horizontally" without needing a hint label.
  const isDesktopWeb = Platform.OS === 'web' && width >= 1024;
  const cardWidth = isDesktopWeb ? Math.max(150, Math.floor((Math.min(width - 260, 1392) - CARD_GAP * 5) / 6)) : Math.round((width - Spacing.base * 2 - CARD_GAP) / 2.2);

  if (!templates.length) return null;

  return (
    <View style={[styles.section, isDesktopWeb && styles.desktopSection]}>
      <Text style={[styles.sectionTitle, { color: theme.colors.textPrimary }]}>{t('plans.templatesTitle')}</Text>
      {!isDesktopWeb ? <Text style={[styles.sectionSubtitle, { color: theme.colors.textMuted }]}>{t('plans.templatesSubtitle')}</Text> : null}
      <FlatList
        data={templates}
        horizontal
        showsHorizontalScrollIndicator={false}
        keyExtractor={item => item.id}
        contentContainerStyle={styles.list}
        renderItem={({ item }) => (
          <TemplateCard template={item} cardWidth={cardWidth} onPress={onSelectTemplate} compact={isDesktopWeb} />
        )}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    marginTop: Spacing.xl,
    marginBottom: Spacing.md,
  },
  desktopSection: { marginTop: 8, marginBottom: 8 },
  sectionTitle: {
    fontSize: Typography.md,
    fontWeight: Typography.extrabold,
    paddingHorizontal: Spacing.base,
  },
  sectionSubtitle: {
    fontSize: Typography.sm,
    marginTop: 2,
    marginBottom: Spacing.md,
    paddingHorizontal: Spacing.base,
  },
  list: {
    gap: CARD_GAP,
    paddingHorizontal: Spacing.base,
    paddingBottom: 2,
  },
  card: {
    borderRadius: 20,
    borderWidth: 1,
    overflow: 'hidden',
    padding: Spacing.md,
    gap: 6,
    minHeight: 150,
  },
  daylightCard: { shadowOpacity: 0, elevation: 0 },
  compactCard: { minHeight: 66, height: 66, borderRadius: 10, padding: 10, flexDirection: 'row', alignItems: 'center', gap: 9 },
  compactEmoji: { fontSize: 20 },
  compactCopy: { flex: 1, minWidth: 0 },
  compactTitle: { fontSize: 12, lineHeight: 15 },
  compactPreview: { fontSize: 9, lineHeight: 12, marginTop: 2 },
  emoji: {
    fontSize: 26,
  },
  title: {
    fontSize: Typography.base,
    fontWeight: Typography.bold,
    lineHeight: 19,
  },
  metaLine: {
    // Restrained brand orange — deliberately fixed rather than theme-swapped,
    // same role as the app's other always-orange accents (e.g. the Create
    // Plan button), and readable against both the dark and light card
    // backgrounds above.
    color: Colors.sunset,
    fontSize: 11,
    fontWeight: Typography.semibold,
  },
  preview: {
    fontSize: 12,
    lineHeight: 16,
  },
});

export default PlanTemplateCarousel;
